// Análise de publicações: descobre se há audiência (data e hora) ou prazo, e monta a sugestão de agendamento.
// Com a IA do usuário ligada, a IA lê a publicação. Sem IA, uma leitura simples do texto procura datas,
// horas e "prazo de N dias". Em qualquer caso, a contagem do prazo é feita aqui, com margem de segurança,
// e o usuário confere e aceita (ou não).
import type { Hit, Task } from '../../store/types';
import { juntarLocal } from '../../lib/local';
import { feriadosDoDia } from '../../lib/feriados';
import { addDays, brYmd, parseYmd, today } from '../../lib/date';
import { perguntarJSON, idiomaResposta } from '../../lib/ia';
import { normSigla } from './tribunais';
import { t } from '../../lib/i18n';

export type Analise = {
  tipo: 'audiencia' | 'prazo' | 'ambos' | 'informativo';
  ato: string;                    // o que é (ex.: "Audiência de instrução", "Intimação para contestar")
  resumo: string;                 // 1 ou 2 frases
  audiencia?: { data: string; hora: string; modalidade?: string; local?: string; link?: string } | null;
  prazo?: { dias?: number | null; contagem?: 'uteis' | 'corridos'; data_final?: string | null; providencia?: string } | null;
  fonte: 'ia' | 'texto';
  em: string;
};

// ─── Tribunal → estado e fuso horário ───────────────────────────────────────────────────────────
const TRT_UF = ['RJ', 'SP', 'MG', 'RS', 'BA', 'PE', 'CE', 'PA', 'PR', 'DF', 'AM', 'SC', 'PB', 'RO', 'SP', 'MA', 'ES', 'GO', 'AL', 'SE', 'RN', 'PI', 'MT', 'MS'];
const TZ_UF: Record<string, string> = {
  AC: 'America/Rio_Branco', AM: 'America/Manaus', RR: 'America/Boa_Vista', RO: 'America/Porto_Velho', MT: 'America/Cuiaba', MS: 'America/Campo_Grande',
  PA: 'America/Belem', AP: 'America/Belem', TO: 'America/Araguaina', MA: 'America/Fortaleza', PI: 'America/Fortaleza', CE: 'America/Fortaleza',
  RN: 'America/Fortaleza', PB: 'America/Fortaleza', PE: 'America/Recife', AL: 'America/Maceio', SE: 'America/Maceio', BA: 'America/Bahia',
};
/** UF do tribunal (TJMS → MS, TRT24 → MS, TRE-PR → PR). Tribunais superiores e federais: DF ou vazio. */
export function ufDoTribunal(sigla: string) {
  const s = normSigla(sigla);
  let m = s.match(/^TJM?([A-Z]{2})$/) || s.match(/^TRE([A-Z]{2})$/);
  if (m) return m[1];
  if (s === 'TJDFT') return 'DF';
  m = s.match(/^TRT(\d{1,2})$/);
  if (m) return TRT_UF[Number(m[1]) - 1] ?? '';
  if (['STF', 'STJ', 'TST', 'TSE', 'STM', 'TRF1'].includes(s)) return 'DF';
  return '';
}
/** Fuso horário do tribunal (a hora da audiência é a hora local do fórum). */
export const fusoDoTribunal = (sigla: string) => { const uf = ufDoTribunal(sigla); return uf ? TZ_UF[uf] ?? 'America/Sao_Paulo' : 'America/Sao_Paulo'; };

// ─── Dias úteis forenses ────────────────────────────────────────────────────────────────────────
/** Dia útil para contagem de prazo: não é sábado, domingo, feriado nacional (ou do estado do tribunal)
 *  nem está no recesso de 20/12 a 20/01 (CPC, art. 220). */
export function diaUtil(d: string, uf = '') {
  const x = parseYmd(d);
  if (x.getDay() === 0 || x.getDay() === 6) return false;
  const md = d.slice(5);
  if (md >= '12-20' || md <= '01-20') return false;
  const regioes = uf ? [uf] : [];
  return !feriadosDoDia(d, { paises: ['BR'], regioes: { BR: regioes } }).some(f => f.tipo !== 'facultativo' || f.nome === 'Carnaval');
}
const proxUtil = (d: string, uf: string) => { let x = addDays(d, 1); while (!diaUtil(x, uf)) x = addDays(x, 1); return x; };

export type ContaPrazo = { publicacao: string; inicio: string; final: string; sugerido: string; dias: number; contagem: 'uteis' | 'corridos' };

/** Prazo a partir da disponibilização no DJEN: publicada no 1º dia útil seguinte; o prazo começa no
 *  1º dia útil depois da publicação. Sugerido = 2 dias úteis antes do fim (margem de segurança). */
export function contarPrazo(disponibilizacao: string, dias: number, contagem: 'uteis' | 'corridos', uf = ''): ContaPrazo {
  const publicacao = proxUtil(disponibilizacao, uf);
  const inicio = proxUtil(publicacao, uf);
  let final = inicio, n = 1;
  if (contagem === 'uteis') { while (n < dias) { final = proxUtil(final, uf); n++; } }
  else { final = addDays(inicio, dias - 1); if (!diaUtil(final, uf)) final = proxUtil(final, uf); }
  let sugerido = final, k = 0;
  while (k < 2) { sugerido = addDays(sugerido, -1); if (diaUtil(sugerido, uf)) k++; }
  if (sugerido < today()) sugerido = today();
  return { publicacao, inicio, final, sugerido, dias, contagem };
}

// ─── Leitura sem IA ─────────────────────────────────────────────────────────────────────────────
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const p2 = (n: number) => String(n).padStart(2, '0');
const NUM: Record<string, number> = { cinco: 5, dez: 10, quinze: 15, oito: 8, trinta: 30, vinte: 20, três: 3, tres: 3, dois: 2, 'quarenta e oito': 2 };

export function analisarPorTexto(h: Hit): Analise {
  const tx = (h.text || '').replace(/\s+/g, ' ');
  const baixo = tx.toLowerCase();
  let audiencia: Analise['audiencia'] = null;
  const idx = baixo.search(/audi[eê]ncia|sess[aã]o de julgamento|per[ií]cia/);
  if (idx >= 0) {
    const trecho = tx.slice(idx, idx + 400);
    let data = '';
    const d1 = trecho.match(/(\d{1,2})[\/.](\d{1,2})[\/.](\d{2,4})/);
    if (d1) { const y = d1[3].length === 2 ? 2000 + Number(d1[3]) : Number(d1[3]); data = `${y}-${p2(Number(d1[2]))}-${p2(Number(d1[1]))}`; }
    else {
      const d2 = trecho.toLowerCase().match(/(\d{1,2})º? de (janeiro|fevereiro|março|marco|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro) de (\d{4})/);
      if (d2) data = `${d2[3]}-${p2(MESES.indexOf(d2[2].replace('marco', 'março')) + 1)}-${p2(Number(d2[1]))}`;
    }
    const hr = trecho.match(/(\d{1,2})\s*(?:h|:|horas?)\s*(\d{2})?(?!\d)/i);
    if (data && data >= today() && hr && Number(hr[1]) < 24) audiencia = { data, hora: `${p2(Number(hr[1]))}:${hr[2] ?? '00'}`,
      modalidade: /virtual|videoconfer|telepresencial|zoom|teams/i.test(trecho) ? 'virtual' : undefined };
  }
  let prazo: Analise['prazo'] = null;
  const pz = baixo.match(/prazo (?:comum )?de (\d{1,3}|[a-zçê ]{3,16}?) \(?(?:\w+\)? )?dias?( [úu]teis| corridos)?/);
  if (pz) {
    const dias = Number(pz[1]) || NUM[pz[1].trim()] || 0;
    if (dias > 0 && dias <= 120) prazo = { dias, contagem: pz[2]?.includes('corrido') ? 'corridos' : 'uteis', providencia: '' };
  } else if (/quarenta e oito horas|48 ?h/.test(baixo)) prazo = { dias: 2, contagem: 'corridos', providencia: '' };
  const tipo = audiencia && prazo ? 'ambos' : audiencia ? 'audiencia' : prazo ? 'prazo' : 'informativo';
  return { tipo, ato: audiencia ? t('Audiência') : prazo ? t('Intimação com prazo') : t('Publicação'), resumo: '', audiencia, prazo, fonte: 'texto', em: new Date().toISOString() };
}

// ─── Leitura com a IA do usuário ───────────────────────────────────────────────────────────────
export async function analisarComIA(h: Hit): Promise<Analise> {
  const sistema = `Você é um assistente jurídico que lê publicações e intimações de diários oficiais brasileiros (DJEN/CNJ e diários municipais).
Extraia apenas o que está escrito; não invente datas. Hoje é ${today()}.
Formato de resposta (JSON):
{"tipo":"audiencia"|"prazo"|"ambos"|"informativo",
 "ato":"nome curto do ato, ex.: Audiência de instrução e julgamento; Intimação para apresentar contestação",
 "resumo":"uma ou duas frases dizendo o que a pessoa precisa fazer",
 "audiencia":{"data":"AAAA-MM-DD","hora":"HH:MM","modalidade":"presencial|virtual|híbrida","local":"endereço ou sala, se houver","link":"link da sala virtual, se houver"} ou null,
 "prazo":{"dias":número ou null,"contagem":"uteis"|"corridos","data_final":"AAAA-MM-DD se a publicação disser a data final, senão null","providencia":"o que deve ser feito no prazo"} ou null}
Regras: "audiencia" só se houver data E hora de audiência, sessão, perícia ou ato presencial marcado. Prazos processuais cíveis e trabalhistas contam em dias úteis; penais em dias corridos, salvo se o texto disser outra coisa. Escreva "ato", "resumo" e "providencia" em ${idiomaResposta()}.`;
  const dados = [`Tribunal: ${h.court}`, `Órgão: ${h.orgao ?? ''}`, `Classe: ${h.classe ?? ''}`, `Tipo: ${h.kind} ${h.tipoDoc ?? ''}`,
    `Processo: ${h.process}`, `Disponibilizada em: ${h.available}`, `Partes: ${(h.partes ?? []).map(p => `${p.nome} (${p.polo})`).join('; ')}`,
    '', 'Texto da publicação:', (h.text || '').slice(0, 12000)].join('\n');
  const r = await perguntarJSON<any>(sistema, dados);
  const okData = (s: any) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
  const okHora = (s: any) => typeof s === 'string' && /^\d{2}:\d{2}$/.test(s);
  const aud = r.audiencia && okData(r.audiencia.data) && okHora(r.audiencia.hora) ? r.audiencia : null;
  const pz = r.prazo && (Number(r.prazo.dias) > 0 || okData(r.prazo.data_final))
    ? { dias: Number(r.prazo.dias) || null, contagem: r.prazo.contagem === 'corridos' ? 'corridos' as const : 'uteis' as const,
        data_final: okData(r.prazo.data_final) ? r.prazo.data_final : null, providencia: String(r.prazo.providencia ?? '') }
    : null;
  return { tipo: aud && pz ? 'ambos' : aud ? 'audiencia' : pz ? 'prazo' : 'informativo', ato: String(r.ato ?? ''), resumo: String(r.resumo ?? ''),
    audiencia: aud, prazo: pz, fonte: 'ia', em: new Date().toISOString() };
}

// ─── Sugestões de agendamento ───────────────────────────────────────────────────────────────────
export type Sugestao = { id: 'audiencia' | 'prazo'; titulo: string; detalhe: string; tarefa: Partial<Task> };

const poloCurto = (p: string) => { const x = p.toUpperCase(); return x === 'A' || x.startsWith('ATIV') ? 'A' : x === 'P' || x.startsWith('PASS') ? 'P' : ''; };
/** "Fulano x Beltrano" (primeiro de cada polo). */
export function partesCurtas(h: Hit) {
  const ps = h.partes ?? [];
  const a = ps.find(p => poloCurto(p.polo) === 'A')?.nome, p = ps.find(x => poloCurto(x.polo) === 'P')?.nome;
  const nome = (s?: string) => (s ? s.split(' ').slice(0, 3).join(' ') : '');
  return a && p ? `${nome(a)} x ${nome(p)}` : nome(a || p || ps[0]?.nome);
}

function notasDe(h: Hit, extra: string[]) {
  return [...extra, '',
    `${t('Processo')}: ${h.process || '-'}`, [h.court, h.orgao].filter(Boolean).join(' · '), h.classe ?? '',
    (h.partes ?? []).map(p => `${p.polo ? p.polo + ': ' : ''}${p.nome}`).join('\n'),
    h.link?.startsWith('https://') ? h.link : '', '', `— ${t('Publicação')} (${brYmd(h.available.slice(0, 10))}) —`, h.text,
  ].filter((x, i, a) => x !== '' || a[i - 1] !== '').join('\n').slice(0, 9000);
}

export function sugestoes(h: Hit, a: Analise): Sugestao[] {
  const out: Sugestao[] = [];
  const proc = h.process || h.court || t('publicação');
  const partes = partesCurtas(h);
  const link = { aba: 'monitor' as const, id: h.hash, label: h.process || h.court || t('Publicação') };
  const uf = ufDoTribunal(h.court);
  if (a.audiencia) {
    const au = a.audiencia;
    out.push({ id: 'audiencia', titulo: `${a.ato || t('Audiência')} · ${brYmd(au.data)} ${au.hora}`,
      detalhe: [au.modalidade, au.local].filter(Boolean).join(' · '),
      tarefa: { title: `${t('Audiência')}: ${proc}${partes ? ' · ' + partes : ''}`, emoji: '⚖️', emojiManual: true, area: 'monitor', energy: 4,
        start: au.data, time: au.hora, duration: 60, tz: fusoDoTribunal(h.court), link,
        alerts: { mode: 'before', before: [60, 1440], itens: [{ tipo: 'antes', min: 1440 }, { tipo: 'antes', min: 60 }] },
        // audiência presencial com endereço: vira o "📍 local" da tarefa (botão de rota no Planner e local na agenda do Google)
        notes: juntarLocal(au.local && !/virtual|telepresencial|online/i.test(`${au.modalidade ?? ''} ${au.local}`) ? `${au.local}${au.local.includes(',') ? '' : ', ' + (h.orgao || '')}` : '',
          notasDe(h, [a.ato, a.resumo, au.local ? `${t('Local')}: ${au.local}` : '', au.link ? `${t('Sala virtual')}: ${au.link}` : ''])) } });
  }
  if (a.prazo) {
    const pz = a.prazo;
    let final = pz.data_final ?? '', sugerido = '', calc = '';
    if (!final && pz.dias) {
      const c = contarPrazo(h.available.slice(0, 10), pz.dias, pz.contagem ?? 'uteis', uf);
      final = c.final; sugerido = c.sugerido;
      calc = t('{n} dias {tipo}: publicada em {pub}, contagem a partir de {ini}, vence em {fim}.', {
        n: c.dias, tipo: c.contagem === 'uteis' ? t('úteis') : t('corridos'), pub: brYmd(c.publicacao), ini: brYmd(c.inicio), fim: brYmd(c.final) });
    } else if (final) {
      let s = final, k = 0;
      while (k < 2) { s = addDays(s, -1); if (diaUtil(s, uf)) k++; }
      sugerido = s < today() ? today() : s;
      calc = t('Vence em {fim} (data informada na publicação).', { fim: brYmd(final) });
    }
    if (final) {
      const prov = pz.providencia || a.ato || t('Prazo');
      out.push({ id: 'prazo', titulo: t('Prazo até {fim} · fazer em {dia}', { fim: brYmd(final), dia: brYmd(sugerido) }), detalhe: calc,
        tarefa: { title: `${t('Prazo')}: ${prov} · ${proc}${partes ? ' · ' + partes : ''}`, emoji: '⏳', emojiManual: true, area: 'monitor', energy: 3,
          start: sugerido, time: null, link, alerts: { mode: 'none', before: [], itens: [{ tipo: 'hora', hora: '08:00' }] },
          notes: notasDe(h, [`⚠️ ${t('Prazo final: {fim}', { fim: brYmd(final) })}`, calc, t('Data sugerida com 2 dias úteis de folga. Confira no sistema oficial.'), a.resumo]),
          subtasks: [{ id: `${h.hash}-p1`, text: t('Conferir o prazo no sistema oficial') }, { id: `${h.hash}-p2`, text: prov }] } });
    }
  }
  return out;
}

export const dataValida = (s: string) => !!s && !isNaN(parseYmd(s).getTime());

// Prévia de demonstração: deixa as contas de prazo acessíveis para conferência pelo navegador.
import { DEMO } from '../../lib/env';
if (DEMO && typeof window !== 'undefined') (window as any).__analise = { contarPrazo, analisarPorTexto, diaUtil, fusoDoTribunal };
